import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Box, Flex, Text } from "@chakra-ui/react";
import Sidebar from "../components/Sidebar";
import SidebarResponsive from "../components/Sidebar/SidebarResponsive";
import AdminNavbar from "../components/Navbars/AdminNavbar";
import MobileBottomNav from "../components/Navbars/MobileBottomNav";
import UserMenu from "../components/Navbars/UserMenu";
import CoreSellLogo from "../components/CoreSellLogo";
import { useAuth } from '../contexts/AuthContext';
import routes from "../routes";
import Dashboard from "../views/Dashboard";
import GatewayProfile from "../views/GatewayProfile";
import Gateways from "../views/Gateways";
import PaymentGateways from "../views/PaymentGateways";
import Products from "../views/Products";
import ProductFormPage from "../views/ProductFormPage";
import ProductCoding from "../views/ProductCoding";
import Profile from "../views/Profile";
import WebhookLogs from "../views/WebhookLogs";
import DigikalaOrders from "../views/DigikalaOrders";
import DigikalaOrdersRequiringStock from "../views/DigikalaOrdersRequiringStock";
import Orders from "../views/Orders";
import SiteSettings from "../views/SiteSettings";
import LandingCMS from "../views/LandingCMS";
import AccountSettings from "../views/AccountSettings";
import WarehouseStock from "../views/WarehouseStock";
import Users from "../views/Users";
import '../styles/scroll.css';

/** hasSite === false → مخفی؛ null/undefined/true → نمایش */
function canAccessSite(user) {
  return user?.hasSite !== false;
}

export default function MainLayout() {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const siteEnabled = canAccessSite(user);
  const visibleRoutes = routes.filter((route) => {
    if (route.superAdminOnly && user?.role !== 'superadmin') return false;
    if (route.requiresSite && !siteEnabled) return false;
    return true;
  });
  
  // Set RTL direction
  document.documentElement.dir = 'rtl';

  const getBrandText = () => {
    const active = visibleRoutes.find((r) => r.path && location.pathname === r.path);
    if (active) return active.rtlName || active.name;
    if (location.pathname.startsWith('/products/new')) return 'افزودن محصول';
    if (location.pathname.includes('/edit')) return 'ویرایش محصول';
    if (location.pathname.includes('/digikala-orders-requiring-stock')) return 'سفارش‌های نیازمند کسر موجودی دیجی‌کالا';
    if (location.pathname.includes('/digikala-orders')) return 'سفارش‌های دیجی‌کالا';
    if (location.pathname.startsWith('/users')) return 'کاربران';
    if (location.pathname.startsWith('/account-settings')) return 'تنظیمات همگام‌سازی';
    if (location.pathname.startsWith('/gateways/')) return 'پروفایل درگاه';
    return "داشبورد";
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="100vh">
        <Text>در حال بارگذاری...</Text>
      </Box>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }
  
  return (
    <Box>
      <Sidebar routes={visibleRoutes} sidebarVariant="opaque" />
      {/* Main Content Area — عرض باقی‌مانده کنار سایدبار ثابت؛ اسکرول افقی ندارد */}
      <Box
        w={{ base: "100%", xl: "auto" }}
        maxW="100%"
        minW={0}
        mr={{ base: 0, xl: "312px" }}
        position="relative"
        minH="100vh"
        bg="gray.50"
        overflowX="hidden"
      >
        {/* Mobile fixed header with logo */}
        <Box
          display={{ base: "flex", lg: "none" }}
          position="fixed"
          top="0"
          left="0"
          right="0"
          zIndex="1000"
          bg="white"
          borderBottomWidth="1px"
          borderBottomColor="gray.200"
          justifyContent="space-between"
          alignItems="center"
          px={4}
          py={2}
        >
          <Box w="40px" />
          <CoreSellLogo to="/dashboard" variant="dark" size="sm" />
          <UserMenu />
        </Box>
        {/* Fixed Header */}
        <Box 
          className="fixed-header transparent-header"
          style={{
            position: 'fixed',
            top: 0,
            right: '312px',
            left: 0,
            zIndex: 1000,
            backgroundColor: 'transparent',
            borderBottom: 'none',
            boxShadow: 'none',
          }}
          display={{ base: "none", lg: "block" }}
        >
          <Box mx="auto" px={{ base: "15px", md: "20px" }} py={{ base: "15px", md: "20px" }}>
            <Flex
              w="100%"
              justifyContent="space-between"
              alignItems="center"
              mb="10px"
              display={{ base: "none", lg: "flex" }}
            >
              <Box>
                <SidebarResponsive routes={visibleRoutes} />
              </Box>
            </Flex>
            <AdminNavbar
              onOpen={() => {}}
              brandText={getBrandText()}
              secondary={false}
              fixed={false}
              routes={visibleRoutes}
            />
          </Box>
        </Box>
        
        <Box
          pt={{ base: "72px", lg: "100px" }}
          px={{ base: "16px", md: "24px" }}
          pb={{ base: "88px", lg: "30px" }}
          minH="100vh"
          minW={0}
          maxW="100%"
          overflowX="hidden"
        >
          {/* Routes */}
          <Routes>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/gateways" element={<Gateways />} />
            <Route path="/gateways/:id" element={<GatewayProfile />} />
            <Route path="/gateways/:id/digikala-orders" element={<DigikalaOrders />} />
            <Route path="/gateways/:id/digikala-orders-requiring-stock" element={<DigikalaOrdersRequiringStock />} />
            <Route path="/payment-gateways" element={<PaymentGateways />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/new" element={<ProductFormPage />} />
            <Route path="/products/:id/edit" element={<ProductFormPage />} />
            <Route path="/product-coding" element={<ProductCoding />} />
            <Route path="/warehouse-stock" element={<WarehouseStock />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/webhook-logs" element={<WebhookLogs />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/account-settings" element={<AccountSettings />} />
            <Route
              path="/users"
              element={user?.role === 'superadmin' ? <Users /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/site-settings"
              element={siteEnabled ? <SiteSettings /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/landing-cms"
              element={siteEnabled ? <LandingCMS /> : <Navigate to="/dashboard" replace />}
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Box>
        <MobileBottomNav />
      </Box>
    </Box>
  );
}
